import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { useAuth } from "@/App";
import { checkPaypalOrder, reportPaypalEvent } from "@/lib/paypalCheckout";
import PaymentSuccessModal from "@/components/PaymentSuccessModal";
import { fmtDate } from "@/lib/practiceAccess";

/**
 * PayPalReturnHandler - what happens when a buyer comes back from PayPal to /wallet?pp_order=<ref>
 * (only used when the server creates the PayPal order itself, "api" mode; in the default "link" mode the buyer never
 * leaves the page and PayPalCheckout does this). We ask the server to confirm the payment with PayPal (never trusting
 * the redirect itself), and show the same success screen as any other purchase. If PayPal is still confirming we keep
 * checking for about 25 seconds; the server finishes the job by itself either way.
 * /wallet?pp_cancel=<ref> is the buyer pressing "Cancel and return" on PayPal.
 */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function PayPalReturnHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshTokens, updateTokens } = useAuth();
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const ref = p.get("pp_order");
    const cancelled = p.get("pp_cancel");
    if (!ref && !cancelled) return undefined;
    const clean = () => navigate(location.pathname, { replace: true });
    if (cancelled) {
      toast.error("Payment cancelled. You weren't charged.");
      track("paypal_cancelled", { source: "return" });
      if (user) reportPaypalEvent("closed_unpaid", { orderRef: cancelled });
      clean();
      return undefined;
    }
    if (!user) return undefined;   // wait for sign-in, then run
    let alive = true;
    (async () => {
      let done = false;
      for (let i = 0; i < 9 && alive && !done; i += 1) {
        try {
          const res = await checkPaypalOrder(ref);
          if (res?.state === "paid") {
            done = true;
            track("paypal_paid", { source: "return", kind: res.kind });
            if (res.kind === "pass") {
              window.dispatchEvent(new Event("formanti:practice-access-changed"));
              toast.success(res.pass_until ? `You're in. Practice is open until ${fmtDate(res.pass_until)}.` : "You're in. Practice is open.");
            } else {
              if (res.balance != null) updateTokens?.(res.balance);
              refreshTokens?.();
              if (alive) setSuccess({ tokens: res.tokens_credited, balance: res.balance, amount: res.amount_label });
            }
          } else if (res?.state === "problem" || res?.state === "failed") {
            done = true;
            track("paypal_failed", { source: "return", reason: res.state });
            toast.error(res.message || "The payment didn't complete. You were not charged.", { duration: 12000 });
          }
        } catch { /* try again */ }
        if (!done) await sleep(3000);
      }
      if (!done && alive) {
        toast.message("PayPal is still confirming your payment. It will be credited automatically within a few minutes.", { duration: 14000 });
      }
      if (alive) clean();
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, user?.id]);

  return (
    <PaymentSuccessModal
      open={!!success}
      onClose={() => setSuccess(null)}
      tokensCredited={success?.tokens}
      newBalance={success?.balance}
      packLabel={null}
      amountLabel={success?.amount}
    />
  );
}
